import { expectArray, expectBoolean, expectNumber, expectOptionalString, expectRecord, expectString } from "./schema";
export const VALIDATION_ISSUE_SEVERITIES = ["blocking", "warning"];
export const OCCURRENCE_LIFECYCLE_STATUSES = ["scheduled", "cancelled", "completed"];
export const OCCURRENCE_RESOLUTION_TYPES = [
    "normal",
    "substitute",
    "reschedule",
    "merge",
    "videoReplacement",
    "cancelOnly"
];
function parseStringEnum(value, allowedValues, path) {
    const parsed = expectString(value, path);
    if (!allowedValues.includes(parsed)) {
        throw new Error(`${path} must be one of: ${allowedValues.join(", ")}`);
    }
    return parsed;
}
function hasOwn(record, key) {
    return Object.prototype.hasOwnProperty.call(record, key);
}
export function parseValidationIssueSeverity(value, path = "validationIssueSeverity") {
    return parseStringEnum(value, VALIDATION_ISSUE_SEVERITIES, path);
}
export function parseOccurrenceLifecycleStatus(value, path = "occurrenceLifecycleStatus") {
    return parseStringEnum(value, OCCURRENCE_LIFECYCLE_STATUSES, path);
}
export function parseOccurrenceResolutionType(value, path = "occurrenceResolutionType") {
    return parseStringEnum(value, OCCURRENCE_RESOLUTION_TYPES, path);
}
export function parseValidationIssue(value, path = "validationIssue") {
    const record = expectRecord(value, path);
    return {
        issueCode: expectString(record.issueCode, `${path}.issueCode`),
        severity: parseValidationIssueSeverity(record.severity, `${path}.severity`),
        message: expectString(record.message, `${path}.message`),
        occurrenceId: expectOptionalString(record.occurrenceId, `${path}.occurrenceId`)
    };
}
export function parseScheduleMonthOccurrenceSummary(value, path = "scheduleMonthOccurrenceSummary") {
    const record = expectRecord(value, path);
    return {
        occurrenceId: expectString(record.occurrenceId, `${path}.occurrenceId`),
        revision: expectNumber(record.revision, `${path}.revision`),
        lessonId: expectString(record.lessonId, `${path}.lessonId`),
        lessonTitle: expectString(record.lessonTitle, `${path}.lessonTitle`),
        sourceType: expectString(record.sourceType, `${path}.sourceType`),
        syncState: expectString(record.syncState, `${path}.syncState`),
        lifecycleStatus: parseOccurrenceLifecycleStatus(record.lifecycleStatus, `${path}.lifecycleStatus`),
        resolutionType: parseOccurrenceResolutionType(record.resolutionType, `${path}.resolutionType`),
        startAt: expectString(record.startAt, `${path}.startAt`),
        endAt: expectString(record.endAt, `${path}.endAt`),
        publicNote: expectOptionalString(record.publicNote, `${path}.publicNote`),
        publishedEver: expectBoolean(record.publishedEver, `${path}.publishedEver`)
    };
}
export function parseScheduleMonthGenerationSummary(value, path = "scheduleMonthGenerationSummary") {
    const record = expectRecord(value, path);
    return {
        generatedCount: expectNumber(record.generatedCount, `${path}.generatedCount`),
        updatedAutoCount: expectNumber(record.updatedAutoCount, `${path}.updatedAutoCount`),
        skippedManualCount: expectNumber(record.skippedManualCount, `${path}.skippedManualCount`),
        skippedShortTermCount: expectNumber(record.skippedShortTermCount, `${path}.skippedShortTermCount`)
    };
}
export function parseScheduleMonth(value, path = "scheduleMonth") {
    const record = expectRecord(value, path);
    return {
        yearMonth: expectString(record.yearMonth, `${path}.yearMonth`),
        status: expectString(record.status, `${path}.status`),
        hasUnpublishedChanges: expectBoolean(record.hasUnpublishedChanges, `${path}.hasUnpublishedChanges`),
        blockingErrorCount: expectNumber(record.blockingErrorCount, `${path}.blockingErrorCount`),
        warningCount: expectNumber(record.warningCount, `${path}.warningCount`),
        draftRevision: expectNumber(record.draftRevision, `${path}.draftRevision`),
        publishedRevision: expectNumber(record.publishedRevision, `${path}.publishedRevision`),
        lastGeneratedAt: expectOptionalString(record.lastGeneratedAt, `${path}.lastGeneratedAt`),
        lastValidatedAt: expectOptionalString(record.lastValidatedAt, `${path}.lastValidatedAt`),
        lastPublishedAt: expectOptionalString(record.lastPublishedAt, `${path}.lastPublishedAt`),
        generationSummary: parseScheduleMonthGenerationSummary(record.generationSummary, `${path}.generationSummary`),
        validationIssues: expectArray(record.validationIssues, parseValidationIssue, `${path}.validationIssues`),
        occurrences: expectArray(record.occurrences, parseScheduleMonthOccurrenceSummary, `${path}.occurrences`)
    };
}
export function parsePublishScheduleMonthRequest(value, path = "publishScheduleMonthRequest") {
    const record = expectRecord(value, path);
    return {
        expectedDraftRevision: expectNumber(record.expectedDraftRevision, `${path}.expectedDraftRevision`)
    };
}
export function parseAdminOccurrenceOverride(value, path = "adminOccurrenceOverride") {
    const record = expectRecord(value, path);
    return {
        overrideId: expectString(record.overrideId, `${path}.overrideId`),
        overrideType: expectString(record.overrideType, `${path}.overrideType`),
        causeType: expectString(record.causeType, `${path}.causeType`),
        status: expectString(record.status, `${path}.status`),
        newDate: expectOptionalString(record.newDate, `${path}.newDate`),
        newStartAt: expectOptionalString(record.newStartAt, `${path}.newStartAt`),
        newEndAt: expectOptionalString(record.newEndAt, `${path}.newEndAt`),
        publicNote: expectOptionalString(record.publicNote, `${path}.publicNote`),
        operatorNote: expectOptionalString(record.operatorNote, `${path}.operatorNote`),
        adminNote: expectOptionalString(record.adminNote, `${path}.adminNote`),
        appliedAt: expectOptionalString(record.appliedAt, `${path}.appliedAt`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`)
    };
}
export function parseAdminOccurrenceDetail(value, path = "adminOccurrenceDetail") {
    const record = expectRecord(value, path);
    return {
        occurrenceId: expectString(record.occurrenceId, `${path}.occurrenceId`),
        yearMonth: expectString(record.yearMonth, `${path}.yearMonth`),
        revision: expectNumber(record.revision, `${path}.revision`),
        lessonId: expectString(record.lessonId, `${path}.lessonId`),
        lessonTitle: expectString(record.lessonTitle, `${path}.lessonTitle`),
        lessonKind: expectString(record.lessonKind, `${path}.lessonKind`),
        sourceType: expectString(record.sourceType, `${path}.sourceType`),
        syncState: expectString(record.syncState, `${path}.syncState`),
        lifecycleStatus: parseOccurrenceLifecycleStatus(record.lifecycleStatus, `${path}.lifecycleStatus`),
        resolutionType: parseOccurrenceResolutionType(record.resolutionType, `${path}.resolutionType`),
        occurrenceDate: expectString(record.occurrenceDate, `${path}.occurrenceDate`),
        startAt: expectString(record.startAt, `${path}.startAt`),
        endAt: expectString(record.endAt, `${path}.endAt`),
        studioId: expectOptionalString(record.studioId, `${path}.studioId`),
        studioDecisionStatus: expectOptionalString(record.studioDecisionStatus, `${path}.studioDecisionStatus`),
        teacherPlatformUserIds: expectArray(record.teacherPlatformUserIds, expectString, `${path}.teacherPlatformUserIds`),
        teacherAssignmentStatus: expectOptionalString(record.teacherAssignmentStatus, `${path}.teacherAssignmentStatus`),
        studioNameSnapshot: expectOptionalString(record.studioNameSnapshot, `${path}.studioNameSnapshot`),
        teacherNameSnapshots: expectArray(record.teacherNameSnapshots, expectString, `${path}.teacherNameSnapshots`),
        publicNote: expectOptionalString(record.publicNote, `${path}.publicNote`),
        operatorNote: expectOptionalString(record.operatorNote, `${path}.operatorNote`),
        adminNote: expectOptionalString(record.adminNote, `${path}.adminNote`),
        publishedEver: expectBoolean(record.publishedEver, `${path}.publishedEver`),
        overrides: expectArray(record.overrides, parseAdminOccurrenceOverride, `${path}.overrides`)
    };
}
export function parseOccurrencePatchRequest(value, path = "occurrencePatchRequest") {
    const record = expectRecord(value, path);
    const parsed = {
        expectedRevision: expectNumber(record.expectedRevision, `${path}.expectedRevision`)
    };
    if (hasOwn(record, "startAt")) {
        parsed.startAt = expectString(record.startAt, `${path}.startAt`);
    }
    if (hasOwn(record, "endAt")) {
        parsed.endAt = expectString(record.endAt, `${path}.endAt`);
    }
    if (hasOwn(record, "studioId")) {
        parsed.studioId = expectOptionalString(record.studioId, `${path}.studioId`);
    }
    if (hasOwn(record, "studioDecisionStatus")) {
        parsed.studioDecisionStatus = expectOptionalString(record.studioDecisionStatus, `${path}.studioDecisionStatus`);
    }
    if (hasOwn(record, "teacherPlatformUserIds")) {
        parsed.teacherPlatformUserIds = expectArray(record.teacherPlatformUserIds, expectString, `${path}.teacherPlatformUserIds`);
    }
    if (hasOwn(record, "teacherAssignmentStatus")) {
        parsed.teacherAssignmentStatus = expectOptionalString(record.teacherAssignmentStatus, `${path}.teacherAssignmentStatus`);
    }
    if (hasOwn(record, "publicNote")) {
        parsed.publicNote = expectOptionalString(record.publicNote, `${path}.publicNote`);
    }
    if (hasOwn(record, "operatorNote")) {
        parsed.operatorNote = expectOptionalString(record.operatorNote, `${path}.operatorNote`);
    }
    if (hasOwn(record, "adminNote")) {
        parsed.adminNote = expectOptionalString(record.adminNote, `${path}.adminNote`);
    }
    return parsed;
}
export function parseApplyOccurrenceOverrideRequest(value, path = "applyOccurrenceOverrideRequest") {
    const record = expectRecord(value, path);
    return {
        expectedRevision: expectNumber(record.expectedRevision, `${path}.expectedRevision`),
        overrideType: expectString(record.overrideType, `${path}.overrideType`),
        causeType: expectString(record.causeType, `${path}.causeType`),
        newDate: expectOptionalString(record.newDate, `${path}.newDate`),
        newStartAt: expectOptionalString(record.newStartAt, `${path}.newStartAt`),
        newEndAt: expectOptionalString(record.newEndAt, `${path}.newEndAt`),
        publicNote: expectOptionalString(record.publicNote, `${path}.publicNote`),
        operatorNote: expectOptionalString(record.operatorNote, `${path}.operatorNote`),
        adminNote: expectOptionalString(record.adminNote, `${path}.adminNote`)
    };
}
